import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import FormContact from "./FormContact";
import AuthIcons from "./AuthIcons";
import ScrollIndicator from "./ScrollIndicator";
import useScrollToTop from "../../hooks/useScollToTop";
import "./Home.css";
import { useUser } from "./UserContext";
import { saveVisite } from "../../services/visitesServices";
import HomeHeroSection from "./HomeHeroSection";
import DiaporamaJudoCoachPro from "./../PresencePro/PricingPage/DiaporamaJudoCoachPro";
import {
  FaArrowRight,
  FaCalendarCheck,
  FaChartLine,
  FaCheckCircle,
  FaGift,
  FaMapMarkedAlt,
  FaRocket,
  FaUsers,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function HomePageVitrine() {
  useScrollToTop();

  const { user } = useUser();
  const navigate = useNavigate();
  const isConnected = !!user;

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");

    if (lastVisitDate !== today) {
      saveVisite({})
        .then(() => {
          localStorage.setItem("lastVisitDate", today);
        })
        .catch((error) => {
          console.error(
            "Erreur lors de l'enregistrement de la visite :",
            error,
          );
        });
    }
  }, []);

  const shortcuts = useMemo(() => {
    if (isConnected) {
      return [
        {
          title: "Gestion des présences",
          description: "Pointer rapidement les présents et absents.",
          icon: FaCalendarCheck,
          action: () => navigate("/gestion-presences"),
          tone: "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
        },
        {
          title: "Lieux & groupes",
          description: "Organiser vos lieux, groupes et créneaux.",
          icon: FaMapMarkedAlt,
          action: () => navigate("/dojos-groupes"),
          tone: "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600",
        },
        {
          title: "Voir les formules",
          description: "Comparer les formules et faire évoluer le club.",
          icon: FaRocket,
          action: () => navigate("/formules-tarifs"),
          tone: "bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600",
        },
      ];
    }

    return [
      {
        title: "Découvrir la démo",
        description: "Créer un compte et tester l’application.",
        icon: FaGift,
        action: () => navigate("/register"),
        tone: "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600",
      },
      {
        title: "Voir les formules",
        description: "Comparer rapidement les offres disponibles.",
        icon: FaChartLine,
        action: () => navigate("/formules-tarifs"),
        tone: "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
      },
      {
        title: "Nous contacter",
        description: "Poser une question ou demander une démonstration.",
        icon: FaUsers,
        action: () => {
          const el = document.getElementById("contact");
          if (el) {
            window.scrollTo({
              top: el.offsetTop - 90,
              behavior: "smooth",
            });
          }
        },
        tone: "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white",
      },
    ];
  }, [isConnected, navigate]);

  const quickPlans = [
    {
      name: "Presence Free",
      price: "Gratuit",
      tone: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10",
      points: ["Petit club", "50 adhérents max", "2 groupes", "1 lieu"],
    },
    {
      name: "Presence",
      price: "10 € / an",
      tone: "border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10",
      points: ["Club complet", "Moins de 100 adhérents", "1 lieu"],
    },
    {
      name: "Presence Pro",
      price: "20 € / an",
      tone: "border-indigo-200 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/10",
      points: ["Club en croissance", "Jusqu’à 200 adhérents", "3 lieux"],
      featured: true,
    },
    {
      name: "Presence Ultim",
      price: "30 € / an",
      tone: "border-violet-200 bg-violet-50 dark:border-violet-500/20 dark:bg-violet-500/10",
      points: ["Grand club", "Plus de 200 adhérents", "Plusieurs lieux"],
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Créez votre compte",
      text: "Commencez en démo ou avec une formule adaptée à votre club.",
      color: "bg-emerald-500",
    },
    {
      number: "2",
      title: "Configurez votre club",
      text: "Ajoutez vos lieux, groupes, enseignants et adhérents.",
      color: "bg-blue-500",
    },
    {
      number: "3",
      title: "Suivez les présences",
      text: "Visualisez rapidement les présents, absents et statistiques.",
      color: "bg-indigo-500",
    },
  ];

  const benefits = [
    {
      icon: FaCalendarCheck,
      title: "Suivi des présences",
      text: "Pointage rapide des adhérents par groupe et par séance.",
    },
    {
      icon: FaUsers,
      title: "Gestion du club",
      text: "Organisation des lieux, groupes, enseignants et adhérents.",
    },
    {
      icon: FaChartLine,
      title: "Vision claire",
      text: "Tableaux de bord et indicateurs pour mieux piloter le club.",
    },
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950">
      {!isConnected && <AuthIcons />}
      <ScrollIndicator />

      <HomeHeroSection isConnected={isConnected} />

      <div id="home-content" className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {/* Bloc utilité */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <div className="max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              Présence Club Pro
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100 sm:text-4xl">
              L’application pour gérer votre club et suivre les présences
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-400 sm:text-base">
              Présence Club Pro permet d’organiser les lieux, les groupes, les
              enseignants et les adhérents, puis de suivre simplement les
              présences, les absences et l’activité du club au quotidien.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {benefits.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                    <Icon />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Raccourcis */}
        {/* <section className="mt-6">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Accès rapide
              </p>
              <h2 className="mt-1 text-2xl font-black text-slate-900 dark:text-slate-100">
                Raccourcis utiles
              </h2>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {shortcuts.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.title}
                  onClick={item.action}
                  className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
                >
                  <div
                    className={cn(
                      "mb-4 flex h-12 w-12 items-center justify-center rounded-2xl transition",
                      item.tone
                    )}
                  >
                    <Icon />
                  </div>

                  <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    {item.description}
                  </p>

                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400">
                    Ouvrir
                    <FaArrowRight className="transition group-hover:translate-x-1" />
                  </div>
                </button>
              );
            })}
          </div>
        </section> */}

        {/* Comment ça fonctionne */}
        <section
          id="register"
          className="mt-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8"
        >
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              Fonctionnement
            </p>
            <h2 className="mt-1 text-2xl font-black text-slate-900 dark:text-slate-100 sm:text-3xl">
              Comment ça marche ?
            </h2>
            <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
              Une mise en route simple pour découvrir l’application puis
              l’utiliser réellement dans votre club.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {steps.map((step) => (
              <div
                key={step.number}
                className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60"
              >
                <div
                  className={cn(
                    "flex h-14 w-14 items-center justify-center rounded-full text-xl font-black text-white",
                    step.color,
                  )}
                >
                  {step.number}
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-slate-100">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 pt-6">
          <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <img
              src="/img/cover/illustration_hero02.webp"
              alt="Enseignants et dirigeants de clubs sportifs utilisant l'application pour faire l'appel et suivre les présences"
              loading="lazy"
              decoding="async"
              className="h-auto w-full object-cover"
            />
          </div>
        </section>

        {/* Aperçu rapide des formules */}
        <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Formules
              </p>
              <h2 className="mt-1 text-2xl font-black text-slate-900 dark:text-slate-100 sm:text-3xl">
                Un aperçu rapide des offres
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                Commencez gratuitement puis choisissez une formule adaptée à la
                taille de votre club.
              </p>
            </div>

            <button
              onClick={() => navigate("/formules-tarifs")}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Voir les formules
              <FaArrowRight />
            </button>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {quickPlans.map((plan) => (
              <div
                key={plan.name}
                className={cn(
                  "rounded-[24px] border p-5",
                  plan.tone,
                  plan.featured &&
                    "ring-2 ring-indigo-200 dark:ring-indigo-500/20",
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                    {plan.name}
                  </h3>
                  {plan.featured && (
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-indigo-700 dark:bg-slate-900 dark:text-indigo-300">
                      Recommandé
                    </span>
                  )}
                </div>

                <p className="mt-4 text-3xl font-black text-slate-900 dark:text-slate-100">
                  {plan.price}
                </p>

                <ul className="mt-4 space-y-2">
                  {plan.points.map((point) => (
                    <li
                      key={point}
                      className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300"
                    >
                      <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div id="diaporama">
        <DiaporamaJudoCoachPro />
      </div>

      <div id="contact">
        <FormContact />
      </div>
    </div>
  );
}

export default HomePageVitrine;
